import {
  AddToCalendarIcon,
  DeleteIcon,
  EditIcon,
  MoveIcon,
  ResourceMenuActions,
  type ResourceMenuItem,
  ValidateIcon,
} from '@all-manager/design-system-v2';
import type { ActivityTimelineFeed } from '../../../../../types';
import DeleteDocumentModal from '../../../../documents/external-components/DeleteDocumentModal/DeleteDocumentModal';
import EditDocumentModal from '../../../../documents/external-components/EditDocumentModal/EditDocumentModal';
import CreateUpdateEventModal from '../../../../events/external-components/CreateUpdateEventModal/CreateUpdateEventModal';
import DeleteEventModal from '../../../../events/external-components/DeleteEventModal/DeleteEventModal';
import AddCallActionModal from '../../actionModales/AddCallActionModal/AddCallActionModal';
import AddIncidentActionModal from '../../actionModales/AddIncidentActionModal/AddIncidentActionModal';
import AddOtherActionModal from '../../actionModales/AddOtherActionModal/AddOtherActionModal';
import AddQuoteApprovedActionModal from '../../actionModales/AddQuoteApprovedActionModal/AddQuoteApprovedActionModal';
import AddQuoteReceivedActionModal from '../../actionModales/AddQuoteReceivedActionModal/AddQuoteReceivedActionModal';
import AddReminderActionModal from '../../actionModales/AddReminderActionModal/AddReminderActionModal';
import DeleteSubjectActionModal from '../../Historic/DeleteSubjectActionModal';
import MoveHistoryItemModal from '../../Historic/MoveHistoryItemModal';
import { useActivityTimelineMenuActions } from './useActivityTimelineMenuActions';

type Props = {
  item: ActivityTimelineFeed;
};

export default function ActivityTimelineMenuActions(props: Props) {
  const hook = useActivityTimelineMenuActions(props);

  if (!hook.isAdmin) {
    return null;
  }

  const actions: (ResourceMenuItem | null | undefined | false)[] = [
    hook.canApproveQuote && {
      id: 'approve',
      label: 'Valider le devis',
      icon: <ValidateIcon fontSize="small" />,
      onClick: hook.handleApproveClick,
    },
    hook.canAddToGoogleCalendar && {
      id: 'google-calendar',
      label: 'Ajouter à mon agenda Google',
      icon: <AddToCalendarIcon fontSize="small" />,
      onClick: hook.handleAddToGoogleCalendar,
    },
    hook.canEdit && {
      id: 'edit',
      label: 'Modifier',
      icon: <EditIcon fontSize="small" />,
      onClick: hook.handleUpdateClick,
    },
    hook.canMove && {
      id: 'move',
      label: 'Déplacer',
      icon: <MoveIcon fontSize="small" />,
      onClick: hook.handleMoveClick,
    },
    hook.canDelete && {
      id: 'delete',
      label: 'Supprimer',
      icon: <DeleteIcon fontSize="small" />,
      onClick: hook.handleDeleteClick,
    },
  ];

  function renderUpdateModal() {
    if (props.item.entity_type === 'event' && props.item.id && props.item.subject_id) {
      return (
        <CreateUpdateEventModal
          eventId={props.item.id}
          subTopicId={props.item.subject_id}
          onClose={hook.handleModalClose}
        />
      );
    }

    if (props.item.entity_type === 'document' && hook.editableDocument) {
      return (
        <EditDocumentModal
          document={hook.editableDocument}
          onClose={hook.handleModalClose}
        />
      );
    }

    if (props.item.subject_id) {
      const modalProps = {
        item: hook.historyItem,
        subjectId: props.item.subject_id,
        onClose: hook.handleModalClose,
      };

      switch (hook.historyItem.action_type) {
        case 'call':
          return <AddCallActionModal {...modalProps} />;
        case 'reminder':
          return <AddReminderActionModal {...modalProps} />;
        case 'incident':
          return <AddIncidentActionModal {...modalProps} />;
        case 'quote_received':
          return <AddQuoteReceivedActionModal {...modalProps} />;
        case 'quote_approved':
          return <AddQuoteApprovedActionModal {...modalProps} />;
        case 'other':
        default:
          return <AddOtherActionModal {...modalProps} />;
      }
    }

    return null;
  }

  function renderDeleteModal() {
    if (props.item.entity_type === 'event') {
      return (
        <DeleteEventModal
          event={hook.event}
          onClose={hook.handleModalClose}
        />
      );
    }

    if (props.item.entity_type === 'document' && hook.documentToDelete) {
      return (
        <DeleteDocumentModal
          document={hook.documentToDelete}
          onClose={hook.handleModalClose}
        />
      );
    }

    return (
      <DeleteSubjectActionModal
        item={hook.historyItem}
        onClose={hook.handleModalClose}
      />
    );
  }

  return (
    <>
      <ResourceMenuActions actions={actions} />

      {hook.activeModal === 'update' ? renderUpdateModal() : null}
      {hook.activeModal === 'delete' ? renderDeleteModal() : null}

      {hook.activeModal === 'approve' && props.item.subject_id ? (
        <AddQuoteApprovedActionModal
          item={hook.historyItem}
          subjectId={props.item.subject_id}
          onClose={hook.handleModalClose}
          isCopy
        />
      ) : null}

      {hook.activeModal === 'move' ? (
        <MoveHistoryItemModal
          item={hook.historyItem}
          onClose={hook.handleModalClose}
        />
      ) : null}
    </>
  );
}

export { ActivityTimelineMenuActions };
